/* @luxottica/vm-remotelog v2.0.9 */
import { LogAppender, LogLevel, Appender, getLogger, configure } from '@log4js2/core';
import { v4 } from 'uuid';
import localforage from 'localforage';
import { DeviceDetection } from '@luxottica/vm-devicedetection';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

class LogMessage {
    constructor(level, logName, message) {
        this.date = new Date().toISOString();
        this.level = level;
        this.message = message;
        this.logName = logName;
    }
    getLevel() {
        return this.level;
    }
    getLogName() {
        return this.logName;
    }
    getMessage() {
        return this.message;
    }
    getDate() {
        return this.date;
    }
}

const dbName = "db_remotelog";
class DBRemoteLogFactory {
    constructor() {
        this.db = localforage.createInstance({ name: dbName });
    }
    static getInstance() {
        if (!DBRemoteLogFactory.instance) {
            DBRemoteLogFactory.instance = new DBRemoteLogFactory();
        }
        return DBRemoteLogFactory.instance;
    }
    getDB() {
        return this.db;
    }
    getLogsTableName() {
        return "logs";
    }
}

class LogsConfigurationRetriever {
    constructor() {
        this.configurationUrl = "https://luxdeepblue.github.io/vm-remotelog-configuration/configuration.json";
        this.currentHostname = window.location.hostname;
    }
    getConfiguration() {
        return new Promise((resolve) => {
            fetch(this.configurationUrl, {
                headers: { "Accept": "application/json", "Accept-Encoding": "gzip" },
                method: "GET",
            }).then((res) => {
                if (res.ok) {
                    return res.json();
                }
                else {
                    resolve(this.defaultConfiguration());
                }
            }).then((json) => {
                if (json[this.currentHostname]) {
                    const config = {
                        appviewSessionIsActive: json[this.currentHostname].appviewSessionIsActive,
                        browserSessionIsActive: json[this.currentHostname].browserSessionIsActive,
                        clientLogIsActive: json[this.currentHostname].clientLogIsActive,
                        serverLogIsActive: json[this.currentHostname].serverLogIsActive,
                        trackingStatusIsActive: json[this.currentHostname].trackingStatusIsActive,
                    };
                    resolve(config);
                }
                else {
                    resolve(this.defaultConfiguration());
                }
            }).catch((e) => {
                resolve(this.defaultConfiguration());
            });
        });
    }
    defaultConfiguration() {
        return {
            appviewSessionIsActive: true,
            browserSessionIsActive: true,
            clientLogIsActive: true,
            serverLogIsActive: false,
            trackingStatusIsActive: true,
        };
    }
}

class LogsConfig {
    constructor() {
        this.configuration = new LogsConfigurationRetriever().getConfiguration();
        this.appViewSessionActive = new Promise((resolve) => {
            this.configuration
                .then((cfg) => {
                resolve(cfg.appviewSessionIsActive);
            })
                .catch((reason) => {
                resolve(true);
            });
        });
        this.browserSessionActive = new Promise((resolve) => {
            this.configuration
                .then((cfg) => {
                resolve(cfg.browserSessionIsActive);
            })
                .catch((reason) => {
                resolve(true);
            });
        });
        this.trackingStatusActive = new Promise((resolve) => {
            this.configuration
                .then((cfg) => {
                resolve(cfg.trackingStatusIsActive);
            })
                .catch((reason) => {
                resolve(true);
            });
        });
        this.clientLogActive = new Promise((resolve) => {
            this.configuration
                .then((cfg) => {
                resolve(cfg.clientLogIsActive);
            })
                .catch((reason) => {
                resolve(true);
            });
        });
        this.serverLogActive = new Promise((resolve) => {
            this.configuration
                .then((cfg) => {
                resolve(cfg.serverLogIsActive);
            })
                .catch((reason) => {
                resolve(true);
            });
        });
    }
    static getInstance() {
        if (!LogsConfig.instance) {
            LogsConfig.instance = new LogsConfig();
        }
        return LogsConfig.instance;
    }
    async isAppViewSessionActive() {
        return this.appViewSessionActive;
    }
    async isBrowserSessionActive() {
        return this.browserSessionActive;
    }
    async isTrackingStatusActive() {
        return this.trackingStatusActive;
    }
    async isServerLogActive() {
        return this.serverLogActive;
    }
    async isClientLogActive() {
        return this.clientLogActive;
    }
}

class RemoteLogService {
    log(message) {
        LogsConfig.getInstance().isServerLogActive().then((isActive) => {
            if (isActive) {
                this.queueMessage(message);
            }
        });
    }
    queueMessage(logMessage) {
        this.dbInstance().getDB().setItem(v4(), {
            date: logMessage.getDate(),
            level: logMessage.getLevel(),
            logName: logMessage.getLogName(),
            message: logMessage.getMessage(),
        });
    }
    dbInstance() {
        return DBRemoteLogFactory.getInstance();
    }
}

let RemoteLogAppender = class RemoteLogAppender extends LogAppender {
    constructor() {
        super(...arguments);
        this.logService = new RemoteLogService();
    }
    static get appenderName() {
        return "RemoteLogAppender";
    }
    append(logEvent) {
        if (logEvent && logEvent.level && logEvent.level <= this.getLogLevel()) {
            this.log(logEvent);
        }
    }
    log(logEvent) {
        switch (logEvent.level) {
            case LogLevel.FATAL:
            case LogLevel.ERROR: {
                if (logEvent.error) {
                    this.logService.log(this.newLogMessage("E", logEvent));
                }
                else {
                    this.logService.log(this.newLogMessage("E", logEvent));
                }
                break;
            }
            case LogLevel.WARN: {
                this.logService.log(this.newLogMessage("W", logEvent));
                break;
            }
            case LogLevel.INFO: {
                this.logService.log(this.newLogMessage("I", logEvent));
                break;
            }
            case LogLevel.DEBUG:
            case LogLevel.TRACE: {
                break;
            }
        }
    }
    newLogMessage(logLevel, logEvent) {
        return new LogMessage(logLevel, logEvent.logger || "", logEvent.message || "");
    }
};
RemoteLogAppender = __decorate([
    Appender("RemoteLogAppender")
], RemoteLogAppender);

const VM_BROWSERSESSION_UUID = "vm.browsersessionuuid.20200120";
const VM_LOGLEVEL_OVERRIDE = "vm.loglevel.override";
const defaultLevel = LogLevel.DEBUG;
const defaultVirtualConsole = false;
const defaultLayout = "%d [%p] %c - %m";
class RemoteLogConfig {
    constructor() {
        this.storage = window.localStorage;
        this.configured = false;
        this.browserUUID = "";
        this.createSessionId();
        this.configureLevel(defaultLevel);
        window.VMAvailableLogLevels = LogLevel;
        window.VMLogLevelsOverrideKey = VM_LOGLEVEL_OVERRIDE;
    }
    static getInstance() {
        if (!RemoteLogConfig.instance) {
            RemoteLogConfig.instance = new RemoteLogConfig();
        }
        return RemoteLogConfig.instance;
    }
    static get storageIdentifier() {
        return VM_BROWSERSESSION_UUID;
    }
    getLogger(moduleName, moduleVersion, loggerName) {
        return getLogger([moduleName, moduleVersion, loggerName].join("."));
    }
    getLoggerInfo(moduleName, moduleVersion, loggerName) {
        return getLogger([moduleName, moduleVersion, loggerName].join("."));
    }
    get currentUuid() {
        return this.browserUUID;
    }
    configureLevel(logLevel) {
        const logOverride = this.storage.getItem(VM_LOGLEVEL_OVERRIDE);
        if (logOverride !== null) {
            const logLevelOverride = logOverride;
            // tslint:disable-next-line: no-console
            console.warn("overriding loglevel with localstorage one ->", logLevelOverride);
            this.configureLog4js2(["Console", RemoteLogAppender], logLevelOverride);
        }
        else {
            try {
                const isActive = LogsConfig.getInstance().isClientLogActive();
                this.configureLog4js2(["Console", RemoteLogAppender], LogLevel.INFO);
                isActive.then((value) => {
                    this.configureLog4js2(["Console", RemoteLogAppender], value ? LogLevel.INFO : LogLevel.ERROR);
                    this.configured = true;
                }).catch((e) => {
                    // tslint:disable-next-line: no-console
                    console.error(e);
                });
            }
            catch (e) {
                // tslint:disable-next-line: no-console
                console.error(e);
                this.configureLog4js2(["Console", RemoteLogAppender], defaultLevel);
            }
        }
    }
    configureLog4js2(logAppenders, logLevel) {
        if (!this.configured) {
            configure({
                appenders: logAppenders,
                layout: defaultLayout,
                level: logLevel,
                virtualConsole: defaultVirtualConsole,
            });
            const appenders = logAppenders.map((appender) => this.logAppenderName(appender));
            // tslint:disable-next-line: no-console
            console.info(`appenders       : ${appenders}
layout          : ${defaultLayout}
level           : ${logLevel}
virtual console : ${defaultVirtualConsole}`);
        }
    }
    logAppenderName(appender) {
        let name = "n.a";
        switch (typeof appender) {
            case "string":
                name = "Console";
                break;
            case "function":
                name = "RemoteLogAppender";
                break;
            default:
                name = "n.a.[" + typeof appender + "]";
                break;
        }
        return name;
    }
    createSessionId() {
        let currentUuid = this.storage.getItem(VM_BROWSERSESSION_UUID);
        if (!currentUuid) {
            const newUuid = v4();
            if (newUuid) {
                this.storage.setItem(VM_BROWSERSESSION_UUID, newUuid);
                currentUuid = newUuid;
            }
        }
        this.browserUUID = currentUuid || "cantcreateuuid";
    }
}

const name="@luxottica/vm-remotelog";const version="2.0.9";

const dbName$1 = "db_appviewsession";
class DBAppViewSessionFactory {
    constructor() {
        this.db = localforage.createInstance({ name: dbName$1 });
    }
    static getInstance() {
        if (!DBAppViewSessionFactory.instance) {
            DBAppViewSessionFactory.instance = new DBAppViewSessionFactory();
        }
        return DBAppViewSessionFactory.instance;
    }
    getDB() {
        return this.db;
    }
    getLogsTableName() {
        return "logs";
    }
}

const logger = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "VmAnalyticsApiConfig");
class VmAnalyticsApiConfig {
    constructor() {
        this.hostTest = "https://vmcore-test.luxottica.com";
        this.hostProd = "https://vmcore.luxottica.com";
        this.appViewSessionApi = "/api/appviewsession";
        this.browserSessionApi = "/api/browsersession";
        this.trackingStatusApi = "/api/trackingstatus";
        this.remoteLogApi = "/api/remotelog";
        this.currentHostname = window.location.hostname;
    }
    get appViewSessionApiEndpoint() {
        return this.getHostname() + this.appViewSessionApi;
    }
    get browserSessionApiEndpoint() {
        return this.getHostname() + this.browserSessionApi;
    }
    get trackingStatusApiEndpoint() {
        return this.getHostname() + this.trackingStatusApi;
    }
    get remoteLogApiEndpoint() {
        return this.getHostname() + this.remoteLogApi;
    }
    getHostname() {
        const host = this.isDevEnvironment() ? this.hostTest : this.hostProd;
        logger.debug("analytics api hostname: {}", host);
        return host;
    }
    isDevEnvironment() {
        const isDev = this.currentHostname === "localhost";
        logger.debug("is dev environment: {}", isDev);
        return isDev;
    }
}

const sendXhrRequest = (method, url, body, responseType, responseHeaders = [], options = {}, onProgress) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method || "GET", url);
        xhr.responseType = responseType;
        for (const header in options) {
            if (options[header]) {
                xhr.setRequestHeader(header, options[header]);
            }
        }
        xhr.onload = (e) => {
            if (xhr.status >= 200 && xhr.status < 300) {
                const responseHeaderValues = {};
                responseHeaders.forEach((header) => {
                    const responseHeaderValue = xhr.getResponseHeader(header);
                    if (responseHeaderValue !== null && responseHeaderValue !== undefined) {
                        responseHeaderValues[header] = responseHeaderValue;
                    }
                });
                resolve({
                    status: xhr.status,
                    statusText: xhr.statusText,
                    response: xhr.response,
                    headers: responseHeaderValues
                });
            }
            else {
                reject({
                    status: xhr.status,
                    statusText: xhr.statusText,
                    response: xhr.response
                });
            }
        };
        xhr.onerror = (e) => {
            reject({
                status: xhr.status,
                statusText: xhr.statusText,
                response: xhr.response
            });
        };
        if (xhr.upload && onProgress) {
            xhr.upload.onprogress = onProgress;
        }
        xhr.send(body);
    });
};

const logger$1 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "AppViewSessionAdapter");
class AppViewSessionAdapter {
    constructor() {
        this.config = new VmAnalyticsApiConfig();
    }
    log(message) {
        const sessionId = RemoteLogConfig.getInstance().currentUuid;
        if (sessionId) {
            const jsonBody = JSON.stringify({
                browserSessionUuid: sessionId,
                date: new Date().toISOString(),
                statusCode: message.statusCode,
                statusText: message.statusText,
            });
            const requestUrl = this.config.appViewSessionApiEndpoint;
            return sendXhrRequest("POST", requestUrl, jsonBody, "", [], {
                "Accept": "application/json",
                "Content-type": "application/json",
            }).catch((e) => {
                return Promise.reject(e);
            });
        }
        else {
            logger$1.warn("sessionId not available");
            return Promise.reject();
        }
    }
}

const logger$2 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "AppViewSessionConsumer");
const dbInstance = () => {
    return DBAppViewSessionFactory.getInstance();
};
const messageAdapter = () => {
    return new AppViewSessionAdapter();
};
const consumeDbRows = () => {
    const dbase = dbInstance().getDB();
    dbase.iterate((value, key) => {
        // if (value === null) {
        //   console.error("undefined: ", { key });
        // }
        messageAdapter().log({
            statusCode: value.statusCode,
            statusText: value.statusText
        }).then(() => {
            logger$2.debug("consumed stored log message: {}", JSON.stringify(value));
            // console.info("consumed: ", {key, value});
            return dbase.removeItem(key);
        }).catch(() => {
            // console.error("failed: ", { key });
        });
    }).then(() => {
        logger$2.debug("all logs consumed from local indexdb");
    });
};
class AppViewSessionConsumer {
    consume() {
        const time1 = window.performance.now();
        consumeDbRows();
        const time2 = window.performance.now();
        logger$2.debug("logs consumed in {} ms", time2 - time1);
    }
}

const logger$3 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "AppViewSessionConsumerJob");
class AppViewSessionConsumerJob {
    constructor() {
        this.started = false;
        logger$3.info("remote log consumer job created");
    }
    static getInstance() {
        if (!AppViewSessionConsumerJob.instance) {
            AppViewSessionConsumerJob.instance = new AppViewSessionConsumerJob();
        }
        return AppViewSessionConsumerJob.instance;
    }
    start() {
        if (this.started === true) {
            logger$3.debug("already started");
        }
        else {
            logger$3.info("start to consume logs");
            this.started = true;
            this.intervalFunction = window.setInterval(new AppViewSessionConsumer().consume, 3000);
        }
    }
    stop() {
        if (this.intervalFunction) {
            window.clearInterval(this.intervalFunction);
        }
    }
}

const dbName$2 = "db_browsersession";
class DBBrowserSessionFactory {
    constructor() {
        this.db = localforage.createInstance({ name: dbName$2 });
    }
    static getInstance() {
        if (!DBBrowserSessionFactory.instance) {
            DBBrowserSessionFactory.instance = new DBBrowserSessionFactory();
        }
        return DBBrowserSessionFactory.instance;
    }
    getDB() {
        return this.db;
    }
    getLogsTableName() {
        return "logs";
    }
}

const logger$4 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "BrowserSessionAdapter");
const VM_BROWSERSESSION_FLAG = RemoteLogConfig.storageIdentifier + ".flag";
const VM_BROWSERSESSION_ETAG = RemoteLogConfig.storageIdentifier + ".etag";
class BrowserSessionAdapter {
    constructor() {
        this.config = new VmAnalyticsApiConfig();
        this.storage = window.localStorage;
    }
    log(message) {
        const deviceDetection = new DeviceDetection();
        const userAgentData = deviceDetection.getUserAgentData();
        const persistedSessionFlag = this.storage.getItem(VM_BROWSERSESSION_FLAG);
        const persistedSessionUuid = RemoteLogConfig.getInstance().currentUuid;
        if (persistedSessionFlag) {
            logger$4.info("skip (session id already created)");
            return Promise.resolve(false);
        }
        else {
            if (persistedSessionUuid) {
                const jsonBody = JSON.stringify({
                    browserSessionUuid: persistedSessionUuid,
                    date: message.date,
                    device: {
                        cpuArchitecture: userAgentData.cpu.architecture,
                        model: userAgentData.device.model,
                        resolution: {
                            dpi: message.deviceResolution.dpi,
                            h: message.deviceResolution.h,
                            w: message.deviceResolution.w,
                        },
                        type: userAgentData.device.type,
                        vendor: userAgentData.device.vendor,
                        webapi: {
                            ambientLight: "ondevicelight" in window,
                            connection: "connection" in navigator,
                            serviceWorker: "serviceWorker" in navigator,
                            share: "share" in navigator,
                            visibility: "hidden" in document,
                        },
                        webcam: {
                            available: message.deviceWebcam.enabled,
                            resolutionW: message.deviceWebcam.resolution,
                        },
                        webgl: {
                            version: message.deviceWebgl.version,
                        },
                    },
                    os: {
                        name: userAgentData.os.name,
                        version: parseFloat(userAgentData.os.version),
                    },
                    userAgent: {
                        browserEngineName: userAgentData.engine.name,
                        browserEngineVersion: parseFloat(userAgentData.engine.version),
                        browserName: userAgentData.browser.name,
                        browserVersion: parseFloat(userAgentData.browser.version),
                        webView: deviceDetection.isWebView(),
                    },
                });
                const requestUrl = this.config.browserSessionApiEndpoint;
                return sendXhrRequest("POST", requestUrl, jsonBody, "", ["etag"], {
                    "Accept": "application/json",
                    "Content-type": "application/json",
                }).then((response) => {
                    const etagsessionId = response.headers.etag;
                    this.storage.setItem(VM_BROWSERSESSION_FLAG, "true");
                    this.storage.setItem(VM_BROWSERSESSION_ETAG, etagsessionId ? etagsessionId.slice(-1, 1) : "n.a.");
                    return true;
                }).catch((e) => {
                    return Promise.reject(e);
                });
            }
            else {
                logger$4.warn("persistent session uuid not available");
                return Promise.reject("persistent session uuid not available");
            }
        }
    }
}

const logger$5 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "BrowserSessionConsumer");
const dbInstance$1 = () => {
    return DBBrowserSessionFactory.getInstance();
};
const messageAdapter$1 = () => {
    return new BrowserSessionAdapter();
};
const consumeDbRows$1 = () => {
    const dbase = dbInstance$1().getDB();
    dbase.iterate((value, key) => {
        // if (value === null) {
        //   console.error("undefined: ", { key });
        // }
        messageAdapter$1().log({
            date: value.date,
            deviceResolution: value.deviceResolution,
            deviceWebcam: value.deviceWebcam,
            deviceWebgl: value.deviceWebgl,
        }).then(() => {
            logger$5.debug("consumed stored log message: {}", JSON.stringify(value));
            // console.info("consumed: ", {key, value});
            return dbase.removeItem(key);
        }).catch(() => {
            // console.error("failed: ", { key });
        });
    }).then(() => {
        logger$5.debug("all logs consumed from local indexdb");
    });
};
class BrowserSessionConsumer {
    consume() {
        const time1 = window.performance.now();
        consumeDbRows$1();
        const time2 = window.performance.now();
        logger$5.debug("logs consumed in {} ms", time2 - time1);
    }
}

const logger$6 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "BrowserSessionConsumerJob");
class BrowserSessionConsumerJob {
    constructor() {
        this.started = false;
        logger$6.info("remote log consumer job created");
    }
    static getInstance() {
        if (!BrowserSessionConsumerJob.instance) {
            BrowserSessionConsumerJob.instance = new BrowserSessionConsumerJob();
        }
        return BrowserSessionConsumerJob.instance;
    }
    start() {
        if (this.started === true) {
            logger$6.debug("already started");
        }
        else {
            logger$6.info("start to consume logs");
            this.started = true;
            this.intervalFunction = window.setInterval(new BrowserSessionConsumer().consume, 3000);
        }
    }
    stop() {
        if (this.intervalFunction) {
            window.clearInterval(this.intervalFunction);
        }
    }
}

const logger$7 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "RemoteLogAdapter");
class RemoteLogAdapter {
    constructor() {
        this.config = new VmAnalyticsApiConfig();
    }
    log(logMessage) {
        try {
            const sessionId = RemoteLogConfig.getInstance().currentUuid;
            if (sessionId) {
                const jsonBody = JSON.stringify({
                    browserSessionUuid: sessionId,
                    date: new Date().toISOString(),
                    level: logMessage.getLevel(),
                    logName: logMessage.getLogName(),
                    message: logMessage.getMessage(),
                });
                const requestUrl = this.config.remoteLogApiEndpoint;
                return sendXhrRequest("POST", requestUrl, jsonBody, "", [], {
                    "Accept": "application/json",
                    "Content-type": "application/json",
                }).catch((e) => {
                    return Promise.reject(e);
                });
            }
            else {
                console.warn("sessionId not available");
                return Promise.reject("sessionId not available");
            }
        }
        catch (e) {
            return Promise.reject(e);
        }
    }
}

const logger$8 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "RemoteLogConsumer");
const dbInstance$2 = () => {
    return DBRemoteLogFactory.getInstance();
};
const messageAdapter$2 = () => {
    return new RemoteLogAdapter();
};
const consumeDbRows$2 = () => {
    const dbase = dbInstance$2().getDB();
    dbase.iterate((value, key) => {
        messageAdapter$2().log(new LogMessage(value.level, value.logName, value.message)).then(() => {
            logger$8.debug("consumed stored log message: {}", JSON.stringify(value));
            // console.info("consumed: ", {key, value});
            return dbase.removeItem(key);
        }).catch(() => {
            // console.error("failed: ", { key });
        });
    }).then(() => {
        logger$8.debug("all logs consumed from local indexdb");
    });
};
class RemoteLogConsumer {
    consume() {
        const time1 = window.performance.now();
        consumeDbRows$2();
        const time2 = window.performance.now();
        logger$8.debug("logs consumed in {} ms", time2 - time1);
    }
}

const logger$9 = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "RemoteLogConsumerJob");
class RemoteLogConsumerJob {
    constructor() {
        this.started = false;
        logger$9.info("remote log consumer job created");
    }
    static getInstance() {
        if (!RemoteLogConsumerJob.instance) {
            RemoteLogConsumerJob.instance = new RemoteLogConsumerJob();
        }
        return RemoteLogConsumerJob.instance;
    }
    start() {
        if (this.started === true) {
            logger$9.debug("already started");
        }
        else {
            logger$9.info("start to consume logs");
            this.started = true;
            this.intervalFunction = window.setInterval(new RemoteLogConsumer().consume, 5000);
        }
    }
    stop() {
        if (this.intervalFunction) {
            window.clearInterval(this.intervalFunction);
        }
    }
}

const dbName$3 = "db_trackingstatus";
class DBTrackingStatusFactory {
    constructor() {
        this.db = localforage.createInstance({ name: dbName$3 });
    }
    static getInstance() {
        if (!DBTrackingStatusFactory.instance) {
            DBTrackingStatusFactory.instance = new DBTrackingStatusFactory();
        }
        return DBTrackingStatusFactory.instance;
    }
    getDB() {
        return this.db;
    }
    getLogsTableName() {
        return "logs";
    }
}

const logger$a = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "TrackingStatusAdapter");
class TrackingStatusAdapter {
    constructor() {
        this.config = new VmAnalyticsApiConfig();
    }
    log(message) {
        const sessionId = RemoteLogConfig.getInstance().currentUuid;
        if (sessionId) {
            let jsonBody;
            if (message.fps) {
                jsonBody = JSON.stringify({
                    browserSessionUuid: sessionId,
                    date: new Date().toISOString(),
                    fps: message.fps,
                });
            }
            else {
                jsonBody = JSON.stringify({
                    browserSessionUuid: sessionId,
                    date: new Date().toISOString(),
                    trackingCode: message.trackingCode,
                    trackingText: message.trackingText,
                });
            }
            const requestUrl = this.config.trackingStatusApiEndpoint;
            return sendXhrRequest("POST", requestUrl, jsonBody, "", [], {
                "Accept": "application/json",
                "Content-type": "application/json",
            }).catch((e) => {
                return Promise.reject(e);
            });
        }
        else {
            console.warn("sessionId not available");
            return Promise.reject("sessionId not available");
        }
    }
}

const logger$b = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "TrackingStatusConsumer");
const dbInstance$3 = () => {
    return DBTrackingStatusFactory.getInstance();
};
const messageAdapter$3 = () => {
    return new TrackingStatusAdapter();
};
const consumeDbRows$3 = () => {
    const dbase = dbInstance$3().getDB();
    dbase.iterate((value, key) => {
        // if (value === null) {
        //   console.error("undefined: ", { key });
        // }
        messageAdapter$3().log({
            fps: value.fps,
            trackingCode: value.trackingCode,
            trackingText: value.trackingText,
        }).then(() => {
            logger$b.debug("consumed stored log message: {}", JSON.stringify(value));
            // console.info("consumed: ", {key, value});
            return dbase.removeItem(key);
        }).catch(() => {
            // console.error("failed: ", { key });
        });
    }).then(() => {
        logger$b.debug("all logs consumed from local indexdb");
    });
};
class TrackingStatusConsumer {
    consume() {
        const time1 = window.performance.now();
        consumeDbRows$3();
        const time2 = window.performance.now();
        logger$b.debug("logs consumed in {} ms", time2 - time1);
    }
}

const logger$c = RemoteLogConfig.getInstance().getLoggerInfo(name, version, "TrackingStatusConsumerJob");
class TrackingStatusConsumerJob {
    constructor() {
        this.started = false;
        logger$c.info("remote log consumer job created");
    }
    static getInstance() {
        if (!TrackingStatusConsumerJob.instance) {
            TrackingStatusConsumerJob.instance = new TrackingStatusConsumerJob();
        }
        return TrackingStatusConsumerJob.instance;
    }
    start() {
        if (this.started === true) {
            logger$c.debug("already started");
        }
        else {
            logger$c.info("start to consume logs");
            this.started = true;
            this.intervalFunction = window.setInterval(new TrackingStatusConsumer().consume, 3000);
        }
    }
    stop() {
        if (this.intervalFunction) {
            window.clearInterval(this.intervalFunction);
        }
    }
}

class JobsStarter {
    constructor() {
        this.createJobs();
    }
    static getInstance() {
        if (!JobsStarter.instance) {
            JobsStarter.instance = new JobsStarter();
        }
        return JobsStarter.instance;
    }
    createJobs() {
        BrowserSessionConsumerJob.getInstance().start();
        AppViewSessionConsumerJob.getInstance().start();
        TrackingStatusConsumerJob.getInstance().start();
        RemoteLogConsumerJob.getInstance().start();
    }
}

class AppViewSessionService {
    log(message) {
        const timestamp = this.now();
        LogsConfig.getInstance().isAppViewSessionActive().then((isActive) => {
            if (isActive) {
                this.queueMessage(message, timestamp);
            }
        });
    }
    queueMessage(message, timestamp) {
        this.dbInstance().getDB().setItem(v4(), {
            date: timestamp,
            statusCode: message.statusCode,
            statusText: message.statusText,
        });
    }
    dbInstance() {
        return DBAppViewSessionFactory.getInstance();
    }
    now() {
        return new Date().toISOString();
    }
}

class BrowserSessionService {
    log(message) {
        const timestamp = this.now();
        LogsConfig.getInstance().isBrowserSessionActive().then((isActive) => {
            if (isActive) {
                this.queueMessage(message, timestamp);
            }
        });
    }
    queueMessage(message, timestamp) {
        this.dbInstance().getDB().setItem(v4(), {
            date: timestamp,
            deviceResolution: message.deviceResolution,
            deviceWebcam: message.deviceWebcam,
            deviceWebgl: message.deviceWebgl,
        });
    }
    dbInstance() {
        return DBBrowserSessionFactory.getInstance();
    }
    now() {
        return new Date().toISOString();
    }
}

class TrackingStatusService {
    log(message) {
        const timestamp = this.now();
        LogsConfig.getInstance().isTrackingStatusActive().then((isActive) => {
            if (isActive) {
                this.queueMessage(message, timestamp);
            }
        });
    }
    queueMessage(message, timestamp) {
        this.dbInstance().getDB().setItem(v4(), {
            date: timestamp,
            fps: message.fps,
            trackingCode: message.trackingCode,
            trackingText: message.trackingText,
        });
    }
    dbInstance() {
        return DBTrackingStatusFactory.getInstance();
    }
    now() {
        return new Date().toISOString();
    }
}

class RemoteLog {
    constructor() {
        this.browserSessionService = new BrowserSessionService();
        this.appViewSessionService = new AppViewSessionService();
        this.trackingStatusService = new TrackingStatusService();
        JobsStarter.getInstance();
    }
    sendBrowserSession(browserSession) {
        this.browserSessionService.log(browserSession);
    }
    sendAppViewSession(...appViewSession) {
        appViewSession.forEach((item) => {
            this.appViewSessionService.log(item);
        });
    }
    sendTracking(...trackingStatus) {
        trackingStatus.forEach((item) => {
            this.trackingStatusService.log(item);
        });
    }
    sendFps(...trackingStatus) {
        trackingStatus.forEach((item) => {
            this.trackingStatusService.log(item);
        });
    }
}

export { RemoteLog, RemoteLogAppender, RemoteLogConfig };
//# sourceMappingURL=vm-remotelog.js.map
